import './action-wrapper';

import type { EditorHost } from '@blocksuite/affine/block-std';
import { ShadowlessElement } from '@blocksuite/affine/block-std';
import { WithDisposable } from '@blocksuite/affine/global/lit';
import { html } from 'lit';
import { property, query } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';

import type { ChatAction } from '../chat-context';

export class ActionMindmap extends WithDisposable(ShadowlessElement) {
  @property({ attribute: false })
  accessor item!: ChatAction;

  @property({ attribute: false })
  accessor host!: EditorHost;

  @query('mini-mindmap-preview')
  private accessor previewEl: HTMLElement | null = null;

  /**
   * Export the mindmap to PNG. We scan the rendered mini-mindmap
   * preview for a canvas (BlockSuite renders the surface to a canvas
   * element) and serialise it. SVG fallback handles styles that
   * render purely as SVG. This is intentionally a thin V1 export —
   * sufficient for researchers to drop a mindmap into a paper or
   * slide deck without round-tripping through screenshot tooling.
   */
  private exportMindmap = async (
    format: 'png' | 'svg'
  ): Promise<void> => {
    const root = this.previewEl;
    if (!root) return;
    const canvas = root.querySelector('canvas');
    const svg = root.querySelector('svg');
    const filenameBase = `mindmap-${Date.now()}`;

    try {
      if (format === 'png') {
        let blob: Blob | null = null;
        if (canvas) {
          blob = await canvasToBlob(canvas as HTMLCanvasElement);
        } else if (svg) {
          blob = await svgElementToPng(svg as SVGSVGElement);
        }
        if (!blob) {
          console.warn('[mindmap-export] no canvas/svg found to export');
          return;
        }
        triggerDownload(blob, `${filenameBase}.png`);
      } else {
        let blob: Blob | null = null;
        if (svg) {
          const text = new XMLSerializer().serializeToString(svg);
          blob = new Blob(
            [`<?xml version="1.0" encoding="UTF-8"?>\n${text}`],
            { type: 'image/svg+xml' }
          );
        } else if (canvas) {
          // BlockSuite renders to canvas — wrap the canvas as a PNG
          // embedded inside an <svg> so the file extension still
          // means something. Not a true vector SVG but it gives the
          // user an exportable doc.
          const dataUrl = (canvas as HTMLCanvasElement).toDataURL('image/png');
          const w = (canvas as HTMLCanvasElement).width;
          const h = (canvas as HTMLCanvasElement).height;
          const wrapped = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">
  <image href="${dataUrl}" width="${w}" height="${h}" />
</svg>`;
          blob = new Blob([wrapped], { type: 'image/svg+xml' });
        }
        if (!blob) {
          console.warn('[mindmap-export] no canvas/svg found to export');
          return;
        }
        triggerDownload(blob, `${filenameBase}.svg`);
      }
    } catch (err) {
      console.error('[mindmap-export] failed:', err);
    }
  };

  protected override render() {
    const answer = this.item.messages[2].content;
    return html`<action-wrapper .host=${this.host} .item=${this.item}>
      <div style=${styleMap({ marginBottom: '12px', height: '140px' })}>
        <mini-mindmap-preview
          .host=${this.host}
          .ctx=${{
            get: () => ({}),
            set: () => {},
          }}
          .answer=${answer}
          .templateShow=${false}
          .height=${140}
        ></mini-mindmap-preview>
      </div>
      <div
        style=${styleMap({
          display: 'flex',
          gap: '6px',
          marginBottom: '8px',
        })}
      >
        <button
          style=${EXPORT_BTN_STYLE}
          @click=${() => void this.exportMindmap('png')}
          title="Download this mindmap as PNG"
        >
          ⇩ PNG
        </button>
        <button
          style=${EXPORT_BTN_STYLE}
          @click=${() => void this.exportMindmap('svg')}
          title="Download this mindmap as SVG"
        >
          ⇩ SVG
        </button>
      </div>
    </action-wrapper>`;
  }
}

const EXPORT_BTN_STYLE = styleMap({
  appearance: 'none',
  background: 'transparent',
  border: '1px solid var(--affine-border-color)',
  borderRadius: '5px',
  color: 'var(--affine-text-secondary-color)',
  fontSize: '11px',
  padding: '3px 9px',
  cursor: 'pointer',
});

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      b => (b ? resolve(b) : reject(new Error('canvas.toBlob returned null'))),
      'image/png'
    );
  });
}

async function svgElementToPng(svg: SVGSVGElement): Promise<Blob> {
  const serialized = new XMLSerializer().serializeToString(svg);
  const svgBlob = new Blob(
    [`<?xml version="1.0" encoding="UTF-8"?>\n${serialized}`],
    { type: 'image/svg+xml;charset=utf-8' }
  );
  const url = URL.createObjectURL(svgBlob);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = reject;
      el.src = url;
    });
    const bbox = svg.viewBox?.baseVal;
    const width =
      bbox && bbox.width ? bbox.width : svg.clientWidth || 480;
    const height =
      bbox && bbox.height ? bbox.height : svg.clientHeight || 240;
    const canvas = document.createElement('canvas');
    const scale = 2;
    canvas.width = Math.max(1, Math.round(width * scale));
    canvas.height = Math.max(1, Math.round(height * scale));
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D context unavailable');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    return await canvasToBlob(canvas);
  } finally {
    URL.revokeObjectURL(url);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'action-mindmap': ActionMindmap;
  }
}
